import React, { useEffect, useMemo, useState } from 'react';
import { RegionLens } from '../lib/socialPulse';
import { useIdentity, IdentityLevel } from '../lib/identity';
import { useUserDP } from '../lib/ledger';
import { useViewport } from '../lib/useViewport';
import {
  mintMarket,
  MINT_BOND_DEFAULT,
  BOND_HARD_FLOOR,
  BOND_DP_COEFFICIENT,
  computeRequiredBond,
  MAKER_FEE_RATE,
  getMintBondBalance,
  ResolutionSource,
  subscribeMintActivity,
  vouchesRequiredFor,
  buildEqualOutcomes,
  MIN_OUTCOMES,
  MAX_OUTCOMES,
  RESOLUTION_CLAUSE_MIN_CHARS,
} from '../lib/mintedMarkets';

// v2.3.1 Minting Cockpit — Permissionless Market Creation, Citizen-gated.
//
// Refactor of the v1.9.3 Minting Facility into a Sovereign-Industrial
// "Cockpit" per the v2.3 Architect brief. The modal is split into two
// columns:
//   LEFT  — Collateral Chamber (Dynamic Bond Lock, 500 − DP × 0.5,
//           floor 100) + Reality Blueprint form (title, outcomes,
//           primary oracle, resolution clause, resolution hierarchy).
//   RIGHT — Projection Feed (Maker Fee + projected earnings based on
//           the creator's estimated volume).
//
// The "Finalize Mint" click runs a short Locking Collateral animation
// (balance ticks down, the bond pulses into a "Bonded" state) before
// the store mutation so the commitment feels tangible rather than
// instantaneous. A dedicated TierGate panel replaces the whole form
// when a sub-Citizen tier tries to open the cockpit.

const MINT_LENSES: { value: RegionLens; label: string }[] = [
  { value: 'GLOBAL', label: 'Global · Macro'    },
  { value: 'ASIA',   label: 'Regional · Asia'   },
  { value: 'EUROPE', label: 'Regional · Europe' },
  { value: 'LOCAL',  label: 'Local'             },
];

const RESOLUTION_SOURCES: { value: ResolutionSource; label: string; hint: string }[] = [
  { value: 'Newswire',       label: 'Newswire',        hint: 'Reuters / AP / Bloomberg feed' },
  { value: 'AI-Consensus',   label: 'AI-Consensus',    hint: 'Multi-model majority vote'     },
  { value: 'Weather-Oracle', label: 'Weather Oracle',  hint: 'OpenWeather / ECMWF feeds'     },
];

// v2.3.1 Dynamic Minting Cockpit — the Creation Bond is no longer a
// fixed 500 OC floor; it's `max(BOND_HARD_FLOOR, 500 − DP × 0.5)`. The
// cockpit reads useUserDP, computes the bond via computeRequiredBond,
// and renders a display-only readout instead of the editable input.

// Volume presets for the Projection Feed. The creator picks a rough
// order of magnitude; exact numbers aren't the point — the sidebar is
// an incentive mirror, not a forecast.
const VOLUME_PRESETS: { label: string; value: number }[] = [
  { label: '1K',    value:   1_000 },
  { label: '10K',   value:  10_000 },
  { label: '100K',  value: 100_000 },
  { label: '1M',    value: 1_000_000 },
];

const DEFAULT_OUTCOMES: string[] = ['', ''];

// Locking-Collateral animation timing. Short enough that the user
// doesn't wait; long enough for the bond transition to read.
const LOCK_ANIM_MS = 1400;

interface Props {
  onClose: () => void;
}

type MintErrorReason =
  | 'INSUFFICIENT_BOND_POOL' | 'TIER_TOO_LOW' | 'BOND_BELOW_MIN' | 'TITLE_TOO_SHORT'
  | 'TOO_FEW_OUTCOMES' | 'TOO_MANY_OUTCOMES' | 'OUTCOME_LABEL_EMPTY' | 'OUTCOME_LABELS_DUPLICATE'
  | 'RESOLUTION_HIERARCHY_EMPTY' | 'PRIMARY_ORACLE_EMPTY' | 'RESOLUTION_CLAUSE_TOO_SHORT';

type WizardStep = 1 | 2 | 3 | 4;

const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  1: 'DEFINITION',
  2: 'RESOLUTION',
  3: 'BOND · ECONOMICS',
  4: 'REVIEW · ANCHOR',
};

export default function MintModal({ onClose }: Props): React.JSX.Element {
  const identity = useIdentity();
  const { isMobile } = useViewport();

  // v2.4 Mobile Tactical Sprint — on < 768 px the Cockpit becomes a
  // four-step wizard. Desktop renders the familiar two-column panel
  // unchanged (isMobile branch only affects layout + step gating).
  const [step, setStep] = useState<WizardStep>(1);

  // v2.3.1 Dynamic Bond — Reputation (DP) discounts the Creation Bond.
  // The derived `requiredBond` is the display-and-submit value; the
  // Cockpit no longer exposes an editable input.
  const userDp       = useUserDP(identity.id ?? '');
  const requiredBond = computeRequiredBond(userDp);
  const bondDiscount = Math.max(0, MINT_BOND_DEFAULT - requiredBond);

  // ── Form state ────────────────────────────────────────────────────
  const [title,            setTitle]            = useState('');
  const [lens,             setLens]             = useState<RegionLens>('GLOBAL');
  const [resolutionSource, setResolutionSource] = useState<ResolutionSource>('Newswire');
  const [primaryOracle,    setPrimaryOracle]    = useState('');
  const [resolutionClause, setResolutionClause] = useState('');
  const [outcomeLabels,    setOutcomeLabels]    = useState<string[]>(DEFAULT_OUTCOMES);
  const [includeField,     setIncludeField]     = useState<boolean>(false);
  const [hierarchyText,    setHierarchyText]    = useState<string>('');
  const [estVolume,        setEstVolume]        = useState<number>(VOLUME_PRESETS[1].value);

  // Bond tracks the derived required value; kept in state so the
  // "Finalize Mint" payload and the Collateral Chamber both read from
  // a single source that stays pinned to the formula as DP changes.
  const [bond,             setBond]             = useState<number>(requiredBond);
  useEffect(() => { setBond(requiredBond); }, [requiredBond]);

  // ── Flow state ────────────────────────────────────────────────────
  const [minted,           setMinted]           = useState(false);
  const [mintedBond,       setMintedBond]       = useState<number>(MINT_BOND_DEFAULT);
  const [mintedLens,       setMintedLens]       = useState<RegionLens>('GLOBAL');
  const [mintedFastTrack,  setMintedFastTrack]  = useState(false);
  const [mintedOutcomes,   setMintedOutcomes]   = useState<number>(0);
  const [error,            setError]            = useState<string | null>(null);
  const [locking,          setLocking]          = useState(false);
  const [bondBalance,      setBondBalance]      = useState<number>(() =>
    identity.id ? getMintBondBalance(identity.id) : 0
  );

  // Tier gate — the cockpit is Citizen+. Observers get the denied
  // panel (no form, no economics sidebar).
  const tierGated = identity.tier < IdentityLevel.CITIZEN;
  const isSovereign = identity.tier >= IdentityLevel.SOVEREIGN;

  useEffect(() => {
    if (!identity.id) return;
    setBondBalance(getMintBondBalance(identity.id));
    return subscribeMintActivity(() => {
      if (identity.id) setBondBalance(getMintBondBalance(identity.id));
    });
  }, [identity.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Hierarchy textarea split on newline OR comma — matches the store's
  // own tolerance so the preview never disagrees with what ships.
  const parsedHierarchy = useMemo(() => {
    return hierarchyText
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }, [hierarchyText]);

  const previewOutcomes = useMemo(() => {
    const labels = outcomeLabels.map(l => l.trim()).filter(l => l.length > 0);
    return buildEqualOutcomes(labels, includeField);
  }, [outcomeLabels, includeField]);

  // ── Validation ────────────────────────────────────────────────────
  const titleValid         = title.trim().length >= 8;
  // v2.3.1 — bond is derived, never user-edited, so validity is purely
  // the floor guard. Leaving the check in place keeps the submit-gate
  // semantics stable if a future rev makes the bond editable again.
  const bondValid          = Number.isFinite(bond) && bond >= BOND_HARD_FLOOR;
  const bondSufficient     = bondBalance >= bond;                   // Task 4: verify pool ≥ required bond before Finalize Mint
  const primaryOracleValid = primaryOracle.trim().length > 0;
  const clauseValid        = resolutionClause.trim().length >= RESOLUTION_CLAUSE_MIN_CHARS;
  const outcomeRowsValid   =
    outcomeLabels.length >= MIN_OUTCOMES &&
    outcomeLabels.length <= MAX_OUTCOMES &&
    outcomeLabels.every(l => l.trim().length > 0);
  const outcomeUniqueValid = (() => {
    const seen: Record<string, boolean> = {};
    let dupe = false;
    outcomeLabels.forEach(l => {
      const k = l.trim().toLowerCase();
      if (!k) return;
      if (seen[k]) dupe = true;
      seen[k] = true;
    });
    return !dupe;
  })();
  const hierarchyValid = parsedHierarchy.length >= 1;

  const canSubmit =
    !tierGated && !locking &&
    titleValid && bondValid && bondSufficient && !!identity.id &&
    outcomeRowsValid && outcomeUniqueValid && hierarchyValid &&
    primaryOracleValid && clauseValid;

  // Per-step validity — Next is enabled only when the current step's
  // required fields are complete, so the user can't paper-chase a
  // missing field on a later step.
  const step1Valid = titleValid && outcomeRowsValid && outcomeUniqueValid;
  const step2Valid = primaryOracleValid && clauseValid && hierarchyValid;
  const step3Valid = bondValid && bondSufficient;
  const stepValid: Record<WizardStep, boolean> = {
    1: step1Valid,
    2: step2Valid,
    3: step3Valid,
    4: canSubmit,
  };

  // Gate a block to either desktop (always shown) or a specific mobile
  // wizard step. Keeps the JSX readable alongside step numbers.
  function showOn(s: WizardStep): boolean {
    return !isMobile || step === s;
  }

  // ── Projection Feed math ──────────────────────────────────────────
  const projectedMakerFee = Math.round(estVolume * MAKER_FEE_RATE * 100) / 100;

  function addOutcome(): void {
    if (outcomeLabels.length >= MAX_OUTCOMES) return;
    setOutcomeLabels([...outcomeLabels, '']);
  }
  function removeOutcome(i: number): void {
    if (outcomeLabels.length <= MIN_OUTCOMES) return;
    const next = outcomeLabels.slice();
    next.splice(i, 1);
    setOutcomeLabels(next);
  }
  function editOutcome(i: number, value: string): void {
    const next = outcomeLabels.slice();
    next[i] = value;
    setOutcomeLabels(next);
  }

  function submit(): void {
    if (!canSubmit || !identity.id) return;
    setError(null);
    setLocking(true);
    // Brief hold so the Locking Collateral animation reads — the bond
    // deduction and mint itself is synchronous; the delay is purely
    // sensory so the viewer sees 500 OC move into the Bonded state.
    window.setTimeout(() => {
      if (!identity.id) return;
      const result = mintMarket({
        title,
        lens,
        resolutionSource,
        primaryOracleUrl:    primaryOracle,
        resolutionClause,
        estimatedVolume:     estVolume,
        creationBond:        bond,
        creatorId:           identity.id,
        creatorTier:         identity.tier,
        outcomeLabels,
        includeField,
        resolutionHierarchy: parsedHierarchy,
      });
      setLocking(false);
      if (!result.ok) {
        setError(mintErrorMessage(result.reason));
        return;
      }
      setMintedBond(bond);
      setMintedLens(lens);
      setMintedFastTrack(result.market.fastTrackedBy !== null);
      setMintedOutcomes(result.market.outcomes.length);
      setMinted(true);
    }, LOCK_ANIM_MS);
  }

  const poolLabel = `${bondBalance.toLocaleString()} OC`;
  const slotCount = previewOutcomes.length;
  const perSlotPct = slotCount > 0 ? (Math.round((1 / slotCount) * 1000) / 10).toFixed(1) : '0.0';
  const mintedBondLabel = `${mintedBond.toLocaleString()} OC`;

  return (
    <div
      onClick={onClose}
      style={{
        position:        'fixed',
        inset:           0,
        backgroundColor: isMobile ? '#02060f' : 'rgba(2,6,23,0.80)',
        backdropFilter:  isMobile ? undefined : 'blur(3px)',
        zIndex:          1000,
        display:         'flex',
        alignItems:      isMobile ? 'stretch' : 'center',
        justifyContent:  'center',
        padding:         isMobile ? 0 : 16,
        overflowY:       'auto',
      }}
    >
      {/* Keyframes for the Locking Collateral animation + bond-pulse. */}
      <style>{`
        @keyframes cockpit-bond-pulse {
          0%, 100% { box-shadow: 0 0 8px rgba(245,158,11,0.35);  transform: scale(1); }
          50%      { box-shadow: 0 0 22px rgba(245,158,11,0.85); transform: scale(1.015); }
        }
        @keyframes cockpit-lock-sweep {
          0%   { transform: translateX(-100%); opacity: 0.0; }
          40%  { opacity: 0.9; }
          100% { transform: translateX(100%); opacity: 0.0; }
        }
        .cockpit-bond-locking { animation: cockpit-bond-pulse 0.7s ease-in-out infinite; }
        .cockpit-lock-sweep {
          position: absolute; inset: 0; pointer-events: none; overflow: hidden; border-radius: inherit;
        }
        .cockpit-lock-sweep::before {
          content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 40%;
          background: linear-gradient(90deg, transparent 0%, rgba(252,211,77,0.35) 50%, transparent 100%);
          animation: cockpit-lock-sweep ${LOCK_ANIM_MS}ms ease-in-out 1;
        }
        @keyframes cockpit-bonded-stamp {
          0%   { opacity: 0; transform: scale(0.6); }
          60%  { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1.0); }
        }
        .cockpit-bonded-stamp { animation: cockpit-bonded-stamp 0.5s ease-out forwards; }

        /* iOS safari zooms on inputs with font-size < 16 px; lift all
           wizard inputs above that threshold and give them a thumb-
           friendly hit area without re-styling every inline inputStyle. */
        .mint-mobile-wizard input,
        .mint-mobile-wizard textarea,
        .mint-mobile-wizard select {
          font-size: 16px !important;
          padding: 10px 12px !important;
          min-height: 44px;
        }
        .mint-mobile-wizard textarea { min-height: 96px; }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        className={isMobile ? 'mint-mobile-wizard' : undefined}
        style={{
          backgroundColor: '#0f172a',
          border:          isMobile ? 'none' : '1px solid #78350f',
          borderTop:       '2px solid #f59e0b',
          borderRadius:    isMobile ? 0 : 10,
          padding:         isMobile ? '16px 14px 14px' : '22px 24px',
          width:           '100%',
          maxWidth:        isMobile ? '100%' : (tierGated ? 520 : 920),
          height:          isMobile ? '100vh' : undefined,
          maxHeight:       isMobile ? '100vh' : '92vh',
          overflowY:       'auto',
          fontFamily:      'monospace',
          boxShadow:       isMobile ? 'none' : '0 0 40px rgba(245,158,11,0.20)',
          display:         'flex',
          flexDirection:   'column',
        }}
      >
        {/* ── Header ───────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <p style={{ color: '#fcd34d', fontSize: 11, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
              ⚒ THE MINTING COCKPIT
            </p>
            <p style={{ color: '#78350f', fontSize: 9, letterSpacing: '0.16em', margin: '2px 0 0' }}>
              SOVEREIGN INDUSTRIAL · CITIZEN-GATED · {isSovereign ? 'SOVEREIGN SEAL' : 'TIER 2'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close Minting Cockpit"
            title="Close (Esc)"
            style={{
              background:      '#1e293b',
              border:          '1px solid #475569',
              color:           '#fcd34d',
              fontSize:        16,
              fontWeight:      700,
              cursor:          'pointer',
              lineHeight:      1,
              padding:         '4px 10px',
              borderRadius:    4,
              fontFamily:      'monospace',
              letterSpacing:   '0.1em',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#78350f'; e.currentTarget.style.borderColor = '#fcd34d'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#1e293b'; e.currentTarget.style.borderColor = '#475569'; }}
          >
            ✕
          </button>
        </div>

        {tierGated ? (
          <TierGate onClose={onClose} />
        ) : minted ? (
          <MintedReceipt
            bondLabel={mintedBondLabel}
            lens={mintedLens}
            outcomes={mintedOutcomes}
            fastTrack={mintedFastTrack}
            onClose={onClose}
          />
        ) : (
        <>
          {/* Mobile Wizard step indicator — shows 4-step progress and
              the current step title. Desktop skips this entirely. */}
          {isMobile && (
            <WizardStepIndicator step={step} />
          )}

          <div style={{
            display:             isMobile ? 'flex' : 'grid',
            flexDirection:       isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : 'minmax(0, 1fr) 260px',
            gap:                 isMobile ? 0 : 18,
            flex:                isMobile ? 1 : undefined,
            minHeight:           isMobile ? 0 : undefined,
            overflowY:           isMobile ? 'auto' : undefined,
          }}>

            {/* ═══ LEFT COLUMN — Collateral Chamber + Reality Blueprint ═══ */}
            <div style={{ minWidth: 0 }}>

              {/* ── Collateral Chamber ─────────────────────────────── */}
              {showOn(3) && (
                <CollateralChamber
                  bondAmount={bond}
                  bondBase={MINT_BOND_DEFAULT}
                  bondDiscount={bondDiscount}
                  balance={bondBalance}
                  bondSufficient={bondSufficient}
                  locking={locking}
                />
              )}

              {/* ── Reality Blueprint heading ─────────────────────── */}
              {showOn(1) && (
                <div style={{
                  display:       'flex',
                  alignItems:    'center',
                  gap:           8,
                  margin:        isMobile ? '2px 0 8px' : '14px 0 8px',
                  paddingBottom: 6,
                  borderBottom:  '1px solid #1e293b',
                }}>
                  <span style={{ color: '#fcd34d', fontSize: 10, letterSpacing: '0.22em', fontWeight: 700 }}>
                    ⊹ REALITY BLUEPRINT
                  </span>
                  <span style={{ color: '#334155', fontSize: 8, letterSpacing: '0.14em' }}>
                    · DEFINE THE TRUTH
                  </span>
                </div>
              )}

              {/* Title */}
              {showOn(1) && (
              <Field label="MARKET TITLE" hint={titleValid ? '' : 'MIN 8 CHARACTERS'}>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Which candidate wins the 2026 French presidential election?"
                  style={inputStyle}
                />
              </Field>
              )}

              {/* Outcomes */}
              {showOn(1) && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 5 }}>
                  <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.16em' }}>
                    OUTCOMES · {slotCount} SLOT{slotCount === 1 ? '' : 'S'} · {perSlotPct}% EACH
                  </span>
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    onClick={addOutcome}
                    disabled={outcomeLabels.length >= MAX_OUTCOMES}
                    style={{
                      background:      outcomeLabels.length >= MAX_OUTCOMES ? '#1a130a' : '#78350f',
                      color:           outcomeLabels.length >= MAX_OUTCOMES ? '#78350f' : '#fcd34d',
                      border:          `1px solid ${outcomeLabels.length >= MAX_OUTCOMES ? '#334155' : '#fcd34d'}`,
                      borderRadius:    4,
                      padding:         '2px 10px',
                      fontSize:        9,
                      fontWeight:      700,
                      letterSpacing:   '0.14em',
                      cursor:          outcomeLabels.length >= MAX_OUTCOMES ? 'not-allowed' : 'pointer',
                      fontFamily:      'monospace',
                    }}
                  >
                    + ADD OUTCOME
                  </button>
                </div>
                {outcomeLabels.map((label, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.14em', width: 22, textAlign: 'right' }}>
                      #{i + 1}
                    </span>
                    <input
                      type="text"
                      value={label}
                      onChange={e => editOutcome(i, e.target.value)}
                      placeholder={i === 0 ? 'e.g. Candidate A' : i === 1 ? 'e.g. Candidate B' : 'e.g. Candidate'}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => removeOutcome(i)}
                      disabled={outcomeLabels.length <= MIN_OUTCOMES}
                      title={outcomeLabels.length <= MIN_OUTCOMES ? `Min ${MIN_OUTCOMES} outcomes` : 'Remove outcome'}
                      style={{
                        background:    outcomeLabels.length <= MIN_OUTCOMES ? '#0f172a' : '#1e293b',
                        color:         outcomeLabels.length <= MIN_OUTCOMES ? '#334155' : '#fca5a5',
                        border:        `1px solid ${outcomeLabels.length <= MIN_OUTCOMES ? '#1e293b' : '#475569'}`,
                        borderRadius:  4,
                        padding:       '4px 8px',
                        fontSize:      11,
                        fontWeight:    700,
                        cursor:        outcomeLabels.length <= MIN_OUTCOMES ? 'not-allowed' : 'pointer',
                        fontFamily:    'monospace',
                        lineHeight:    1,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {!outcomeRowsValid && (
                  <p style={{ color: '#fca5a5', fontSize: 8, letterSpacing: '0.14em', margin: '4px 0 0' }}>
                    EVERY OUTCOME NEEDS A NAME · MIN {MIN_OUTCOMES} · MAX {MAX_OUTCOMES}
                  </p>
                )}
                {!outcomeUniqueValid && (
                  <p style={{ color: '#fca5a5', fontSize: 8, letterSpacing: '0.14em', margin: '4px 0 0' }}>
                    OUTCOME LABELS MUST BE UNIQUE
                  </p>
                )}
              </div>
              )}

              {/* Include The Field */}
              {showOn(1) && (
              <label style={{
                display:         'flex',
                alignItems:      'flex-start',
                gap:             10,
                padding:         '8px 10px',
                marginBottom:    12,
                border:          `1px solid ${includeField ? '#fcd34d' : '#334155'}`,
                borderRadius:    6,
                backgroundColor: includeField ? 'rgba(252,211,77,0.06)' : '#1e293b',
                cursor:          'pointer',
                transition:      'border-color 0.15s, background-color 0.15s',
              }}>
                <input
                  type="checkbox"
                  checked={includeField}
                  onChange={e => setIncludeField(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ flex: 1 }}>
                  <span style={{ color: includeField ? '#fcd34d' : '#94a3b8', fontSize: 10, letterSpacing: '0.14em', fontWeight: 700, display: 'block' }}>
                    INCLUDE "THE FIELD"
                  </span>
                  <span style={{ color: '#64748b', fontSize: 8, letterSpacing: '0.12em', display: 'block', marginTop: 3, lineHeight: 1.5 }}>
                    Catch-all outcome · wins if an entity NOT in the list is declared the winner by the Resolution Source
                  </span>
                </span>
              </label>
              )}

              {/* Lens */}
              {showOn(1) && (
              <Field
                label="LENS TAGGING"
                hint={
                  isSovereign
                    ? 'SOVEREIGN SELF-MINT — AUTO-VOUCHED · GOES LIVE INSTANTLY'
                    : lens === 'GLOBAL'
                      ? `§6 · ${vouchesRequiredFor(lens)} CITIZEN VOUCHES OR 1 SOVEREIGN TO PROMOTE`
                      : `§6 · ${vouchesRequiredFor(lens)} CITIZEN VOUCHES TO PROMOTE`
                }
              >
                <select
                  value={lens}
                  onChange={e => setLens(e.target.value as RegionLens)}
                  style={inputStyle}
                >
                  {MINT_LENSES.map(o => (
                    <option key={o.value} value={o.value} style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>
                      {o.label} · {vouchesRequiredFor(o.value)} vouch{vouchesRequiredFor(o.value) === 1 ? '' : 'es'}
                    </option>
                  ))}
                </select>
              </Field>
              )}

              {showOn(1) && isSovereign && (
                <div style={{
                  display:         'flex',
                  alignItems:      'center',
                  gap:             8,
                  padding:         '8px 10px',
                  marginBottom:    12,
                  border:          '1px solid #fcd34d',
                  borderRadius:    6,
                  backgroundColor: 'rgba(252,211,77,0.08)',
                  boxShadow:       '0 0 10px rgba(252,211,77,0.15)',
                }}>
                  <span style={{ color: '#fcd34d', fontSize: 11, letterSpacing: '0.22em', fontWeight: 800 }}>
                    ✦ SOVEREIGN SEAL
                  </span>
                  <span style={{ flex: 1 }} />
                  <span style={{ color: '#fcd34d', fontSize: 8, letterSpacing: '0.14em', textAlign: 'right', lineHeight: 1.4 }}>
                    Self-mint is auto-vouched. Market enters ACTIVE on the<br />
                    selected lens at T+0 — bypasses the Citizen queue.
                  </span>
                </div>
              )}

              {/* Resolution Source (canonical adjudicator type) */}
              {showOn(2) && (
              <Field
                label="RESOLUTION SOURCE"
                hint={RESOLUTION_SOURCES.find(s => s.value === resolutionSource)?.hint ?? ''}
              >
                <select
                  value={resolutionSource}
                  onChange={e => setResolutionSource(e.target.value as ResolutionSource)}
                  style={inputStyle}
                >
                  {RESOLUTION_SOURCES.map(o => (
                    <option key={o.value} value={o.value} style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              )}

              {/* Primary Oracle / Source — free-form URL or agency. */}
              {showOn(2) && (
              <Field
                label="PRIMARY ORACLE · SOURCE OF TRUTH"
                hint={
                  primaryOracleValid
                    ? 'URL OR AGENCY · FIRST READ AT RESOLUTION'
                    : 'REQUIRED · e.g. https://reuters.com OR "Associated Press"'
                }
              >
                <input
                  type="text"
                  value={primaryOracle}
                  onChange={e => setPrimaryOracle(e.target.value)}
                  placeholder='https://reuters.com/world/  ·  or  ·  "Associated Press"'
                  style={inputStyle}
                />
              </Field>
              )}

              {/* Resolution Clause — narrative winning condition. */}
              {showOn(2) && (
              <Field
                label="RESOLUTION CLAUSE"
                hint={
                  clauseValid
                    ? `§RB · ${resolutionClause.trim().length} CHARS · CONDITIONS DEFINED`
                    : `DEFINE THE EXACT CONDITIONS UNDER WHICH THIS TRUTH BECOMES KNOWABLE · MIN ${RESOLUTION_CLAUSE_MIN_CHARS} CHARS`
                }
              >
                <textarea
                  value={resolutionClause}
                  onChange={e => setResolutionClause(e.target.value)}
                  placeholder={'e.g. "Resolves YES if the Election Commission declares Candidate A as the winner of the presidential election on or before 2027-05-10. Otherwise resolves NO."'}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'monospace', lineHeight: 1.5 }}
                />
              </Field>
              )}

              {/* Resolution Hierarchy */}
              {showOn(2) && (
              <Field
                label="RESOLUTION HIERARCHY"
                hint={
                  hierarchyValid
                    ? `§3 · ${parsedHierarchy.length} SOURCE${parsedHierarchy.length === 1 ? '' : 'S'} · FIRST MATCH WINS`
                    : 'AT LEAST ONE SOURCE REQUIRED · ONE PER LINE OR COMMA-SEPARATED'
                }
              >
                <textarea
                  value={hierarchyText}
                  onChange={e => setHierarchyText(e.target.value)}
                  placeholder={'Official Election Commission results\nReuters\nAssociated Press'}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 54, fontFamily: 'monospace' }}
                />
              </Field>
              )}

              {/* v2.3.1 Dynamic Bond — display-only Market Entry Cost.
                  Formula: max(BOND_HARD_FLOOR, 500 − DP × 0.5).
                  Shows the DP-derived discount inline so the creator
                  can see the Trust Matrix working. */}
              {showOn(3) && (
              <Field
                label="MARKET ENTRY COST · DYNAMIC BOND"
                hint={
                  bondDiscount > 0
                    ? `TRUST MATRIX · ${userDp.toFixed(0)} DP · DISCOUNT ${bondDiscount.toFixed(0)} OC · RETURNED ON RESOLUTION + ${(MAKER_FEE_RATE * 100).toFixed(1)}% MAKER FEE`
                    : `NO DP DISCOUNT YET · FLOOR ${BOND_HARD_FLOOR} OC · EARN DP BY PLEDGING`
                }
              >
                <div style={{
                  display:        'flex',
                  alignItems:     'center',
                  gap:            8,
                  padding:        '8px 10px',
                  borderRadius:   6,
                  border:         '1px solid #78350f',
                  background:     'rgba(120,53,15,0.10)',
                }}>
                  <span style={{ color: '#fde047', fontSize: 18, fontWeight: 900, letterSpacing: '0.04em' }}>
                    {bond.toLocaleString()}
                  </span>
                  <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em' }}>OC</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ color: '#78350f', fontSize: 8, letterSpacing: '0.18em', fontFamily: 'monospace' }}>
                    {MINT_BOND_DEFAULT} − {userDp.toFixed(0)} × {BOND_DP_COEFFICIENT} = {bond.toLocaleString()}
                  </span>
                </div>
              </Field>
              )}

              {/* Mobile Step 4 · Final Review — summary of everything
                  the creator is about to anchor. Desktop shows the form
                  all at once, so this panel only renders on mobile. */}
              {isMobile && step === 4 && (
                <MobileReviewPanel
                  title={title}
                  lens={lens}
                  isSovereign={isSovereign}
                  outcomes={previewOutcomes}
                  includeField={includeField}
                  resolutionSource={resolutionSource}
                  primaryOracle={primaryOracle}
                  resolutionClause={resolutionClause}
                  parsedHierarchy={parsedHierarchy}
                  bond={bond}
                  bondDiscount={bondDiscount}
                  bondBase={MINT_BOND_DEFAULT}
                  bondBalance={bondBalance}
                  bondSufficient={bondSufficient}
                  locking={locking}
                  estVolume={estVolume}
                  projectedMakerFee={projectedMakerFee}
                />
              )}

              {/* Error + bond guard */}
              {showOn(4) && error && (
                <p style={{
                  color:         '#fca5a5',
                  fontSize:      10,
                  letterSpacing: '0.1em',
                  margin:        '0 0 10px',
                  padding:       '6px 8px',
                  border:        '1px solid #450a0a',
                  borderRadius:  4,
                  background:    'rgba(69,10,10,0.25)',
                }}>
                  {error}
                </p>
              )}
              {(!isMobile || step === 3 || step === 4) && !bondSufficient && !error && (
                <p style={{
                  color:         '#fca5a5',
                  fontSize:      9,
                  letterSpacing: '0.12em',
                  margin:        '0 0 10px',
                }}>
                  BOND POOL BELOW {bond.toLocaleString()} OC — REDUCE BOND OR WAIT FOR AN AUTO-REFUND TO TOP UP.
                </p>
              )}

              {/* Actions — desktop renders this row; the mobile wizard
                  has its own Back / Next / Finalize footer below. */}
              {!isMobile && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
                <button
                  onClick={onClose}
                  disabled={locking}
                  style={{
                    padding:         '7px 16px',
                    borderRadius:    6,
                    border:          '1px solid #334155',
                    backgroundColor: 'transparent',
                    color:           '#64748b',
                    fontSize:        10,
                    cursor:          locking ? 'not-allowed' : 'pointer',
                    fontFamily:      'monospace',
                    letterSpacing:   '0.12em',
                  }}
                >
                  CANCEL
                </button>
                <button
                  onClick={submit}
                  disabled={!canSubmit}
                  style={{
                    padding:         '7px 20px',
                    borderRadius:    6,
                    border:          `1px solid ${canSubmit ? '#f59e0b' : '#78350f'}`,
                    backgroundColor: canSubmit ? '#f59e0b' : '#1a130a',
                    color:           canSubmit ? '#1a0f00' : '#78350f',
                    fontSize:        10,
                    cursor:          canSubmit ? 'pointer' : 'not-allowed',
                    fontFamily:      'monospace',
                    letterSpacing:   '0.14em',
                    fontWeight:      800,
                  }}
                  title={!bondSufficient ? `Requires ≥ ${bond.toLocaleString()} OC in your Mint Bond Pool` : undefined}
                >
                  {locking ? '⚒ LOCKING COLLATERAL…' : '⚒ FINALIZE MINT'}
                </button>
              </div>
              )}
            </div>

            {/* ═══ RIGHT COLUMN — Projection Feed — desktop always,
                mobile only on Step 3 · Bond · Economics ═══ */}
            {showOn(3) && (
              <ProjectionFeed
                poolLabel={poolLabel}
                bond={bond}
                estVolume={estVolume}
                setEstVolume={setEstVolume}
                projectedMakerFee={projectedMakerFee}
              />
            )}
          </div>

          {/* Mobile Wizard footer — Back / step-dot / Next or Finalize. */}
          {isMobile && (
            <WizardFooter
              step={step}
              canAdvance={stepValid[step]}
              locking={locking}
              onBack={() => {
                if (step === 1) { onClose(); return; }
                setStep((step - 1) as WizardStep);
              }}
              onNext={() => {
                if (step === 4) { submit(); return; }
                if (!stepValid[step]) return;
                setStep((step + 1) as WizardStep);
              }}
            />
          )}
        </>
        )}
      </div>
    </div>
  );
}

// ─── Collateral Chamber ────────────────────────────────────────────────
// Prominent Bond Lock visual. During `locking` the panel pulses + a
// gold sweep crosses it; afterward the bond sits in a "BONDED" stamp
// state (handled by MintedReceipt for the post-mint moment).
function CollateralChamber({
  bondAmount, bondBase, bondDiscount, balance, bondSufficient, locking,
}: {
  bondAmount:   number;
  bondBase:     number;
  bondDiscount: number;
  balance:      number;
  bondSufficient: boolean;
  locking:      boolean;
}): React.JSX.Element {
  const balanceAfter = Math.max(0, balance - bondAmount);
  const discounted   = bondDiscount > 0;
  return (
    <div
      className={locking ? 'cockpit-bond-locking' : undefined}
      style={{
        position:        'relative',
        border:          `1px solid ${locking ? '#fcd34d' : bondSufficient ? '#78350f' : '#7f1d1d'}`,
        borderLeft:      `3px solid ${locking ? '#fcd34d' : '#f59e0b'}`,
        borderRadius:    8,
        padding:         '14px 16px',
        background:      'linear-gradient(180deg, rgba(120,53,15,0.20) 0%, rgba(15,23,42,0.60) 100%)',
        boxShadow:       locking ? '0 0 20px rgba(245,158,11,0.55)' : '0 0 8px rgba(245,158,11,0.15)',
        overflow:        'hidden',
        transition:      'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      {locking && <div className="cockpit-lock-sweep" />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ color: '#fcd34d', fontSize: 10, letterSpacing: '0.22em', fontWeight: 800 }}>
          ⊡ COLLATERAL CHAMBER
        </span>
        <span style={{ color: locking ? '#fcd34d' : '#78350f', fontSize: 8, letterSpacing: '0.22em', fontWeight: 700 }}>
          {locking ? '· LOCKING ·' : '· ARMED ·'}
        </span>
      </div>

      {/* Bond Lock — the dominant number. Discounted bonds render the
          base alongside a strike-through so the Trust-Matrix reduction
          reads visually: 500 OC → 425 OC. */}
      <div style={{
        display:       'flex',
        alignItems:    'baseline',
        gap:           8,
        marginBottom:  4,
      }}>
        {discounted && (
          <span style={{
            color:          '#64748b',
            fontSize:       18,
            fontWeight:     700,
            letterSpacing:  '0.04em',
            textDecoration: 'line-through',
            opacity:        0.7,
          }}>
            {bondBase.toLocaleString()}
          </span>
        )}
        <span style={{ color: '#fde047', fontSize: 34, fontWeight: 900, letterSpacing: '0.04em' }}>
          {bondAmount.toLocaleString()}
        </span>
        <span style={{ color: '#f59e0b', fontSize: 14, fontWeight: 700, letterSpacing: '0.22em' }}>
          OC
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: '#78350f', fontSize: 8, letterSpacing: '0.22em' }}>
          {discounted
            ? `DYNAMIC BOND · − ${bondDiscount.toLocaleString()} OC`
            : 'DYNAMIC BOND · FLOOR 100'}
        </span>
      </div>

      {/* Balance before/after deduction. Visualises the transfer. */}
      <div style={{
        display:       'flex',
        alignItems:    'center',
        gap:           10,
        marginTop:     10,
        padding:       '7px 10px',
        borderRadius:  5,
        background:    '#0b1220',
        border:        '1px solid #1e293b',
      }}>
        <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.14em' }}>BOND POOL</span>
        <span style={{ color: '#e2e8f0', fontSize: 11, fontWeight: 700 }}>
          {balance.toLocaleString()}
        </span>
        <span style={{ color: locking ? '#fcd34d' : '#475569', fontSize: 13, letterSpacing: '0.2em' }}>
          {locking ? '⟶' : '→'}
        </span>
        <span style={{
          color:      bondSufficient ? (locking ? '#fcd34d' : '#fde047') : '#fca5a5',
          fontSize:   11,
          fontWeight: 800,
          transition: 'color 0.2s',
        }}>
          {balanceAfter.toLocaleString()}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{
          color:         locking ? '#fcd34d' : bondSufficient ? '#78350f' : '#7f1d1d',
          fontSize:      8,
          letterSpacing: '0.2em',
          fontWeight:    700,
        }}>
          {locking ? 'BONDING…' : bondSufficient ? 'READY' : 'INSUFFICIENT'}
        </span>
      </div>
    </div>
  );
}

// ─── Projection Feed (Maker Fees) ─────────────────────────────────────
function ProjectionFeed({
  poolLabel, bond, estVolume, setEstVolume, projectedMakerFee,
}: {
  poolLabel: string;
  bond: number;
  estVolume: number;
  setEstVolume: (n: number) => void;
  projectedMakerFee: number;
}): React.JSX.Element {
  return (
    <div style={{
      border:        '1px solid #1e293b',
      borderRadius:  8,
      padding:       '14px 14px',
      background:    '#0b1220',
      alignSelf:     'start',
      display:       'flex',
      flexDirection: 'column',
      gap:           12,
      minWidth:      0,
    }}>
      <span style={{ color: '#94a3b8', fontSize: 10, letterSpacing: '0.22em', fontWeight: 700 }}>
        ⟜ PROJECTION FEED
      </span>

      {/* Maker Fee rate */}
      <div style={{
        border:        '1px solid #1e293b',
        borderRadius:  5,
        padding:       '9px 10px',
        background:    '#0f172a',
      }}>
        <div style={{ color: '#64748b', fontSize: 8, letterSpacing: '0.22em', marginBottom: 4 }}>
          MAKER FEE
        </div>
        <div style={{ color: '#fcd34d', fontSize: 18, fontWeight: 800, letterSpacing: '0.04em' }}>
          {(MAKER_FEE_RATE * 100).toFixed(1)}%
        </div>
        <div style={{ color: '#334155', fontSize: 8, letterSpacing: '0.14em', marginTop: 3 }}>
          OF SETTLED VOLUME · PAID TO CREATOR
        </div>
      </div>

      {/* Volume estimator */}
      <div>
        <div style={{ color: '#64748b', fontSize: 8, letterSpacing: '0.22em', marginBottom: 6 }}>
          ESTIMATED VOLUME · OC
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {VOLUME_PRESETS.map(p => {
            const active = estVolume === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setEstVolume(p.value)}
                style={{
                  flex:          1,
                  padding:       '5px 0',
                  borderRadius:  4,
                  border:        `1px solid ${active ? '#fcd34d' : '#334155'}`,
                  background:    active ? '#78350f' : '#1e293b',
                  color:         active ? '#fcd34d' : '#94a3b8',
                  fontSize:      9,
                  fontWeight:    700,
                  letterSpacing: '0.14em',
                  cursor:        'pointer',
                  fontFamily:    'monospace',
                  transition:    'all 0.15s',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Projected earnings */}
      <div style={{
        border:        '1px solid #78350f',
        borderRadius:  5,
        padding:       '10px 10px',
        background:    'rgba(120,53,15,0.16)',
      }}>
        <div style={{ color: '#78350f', fontSize: 8, letterSpacing: '0.22em', marginBottom: 4, fontWeight: 700 }}>
          PROJECTED EARNINGS
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ color: '#fde047', fontSize: 22, fontWeight: 900, letterSpacing: '0.03em' }}>
            {projectedMakerFee.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
          <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em' }}>
            OC
          </span>
        </div>
        <div style={{ color: '#334155', fontSize: 8, letterSpacing: '0.14em', marginTop: 4, lineHeight: 1.4 }}>
          {estVolume.toLocaleString()} OC × {(MAKER_FEE_RATE * 100).toFixed(1)}%
          <br />
          INCENTIVE · MINT WHAT CITIZENS CARE ABOUT
        </div>
      </div>

      {/* Bond context — keeps the pool readout adjacent to the
          economics story so the creator sees the ratio. */}
      <div style={{
        display:       'flex',
        flexDirection: 'column',
        gap:           4,
        padding:       '9px 10px',
        borderRadius:  5,
        border:        '1px dashed #1e293b',
        background:    'transparent',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <span style={{ color: '#64748b', fontSize: 8, letterSpacing: '0.22em' }}>
            MINT BOND POOL
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ color: '#e2e8f0', fontSize: 11, fontWeight: 700 }}>{poolLabel}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <span style={{ color: '#64748b', fontSize: 8, letterSpacing: '0.22em' }}>
            STAKE THIS MINT
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 700 }}>
            {bond.toLocaleString()} OC
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Tier Gate ─────────────────────────────────────────────────────────
function TierGate({ onClose }: { onClose: () => void }): React.JSX.Element {
  return (
    <div style={{
      textAlign: 'center',
      padding:   '18px 8px 6px',
    }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
      <p style={{ color: '#fcd34d', fontSize: 13, letterSpacing: '0.18em', margin: 0, fontWeight: 800 }}>
        TIER 2 · CITIZEN REQUIRED
      </p>
      <p style={{ color: '#94a3b8', fontSize: 11, letterSpacing: '0.04em', margin: '10px 0 0', lineHeight: 1.6 }}>
        Reputation (DP) threshold not met. Reach Level 2 to Mint.
      </p>
      <p style={{ color: '#94a3b8', fontSize: 10, letterSpacing: '0.04em', margin: '10px 0 0', lineHeight: 1.6 }}>
        Minting a market locks a Dynamic Bond Collateral — 500 OC at
        zero DP, discounted down to {BOND_HARD_FLOOR} OC as your Trust
        Matrix grows — and commits you to a Reality Blueprint.
      </p>
      <p style={{ color: '#64748b', fontSize: 10, letterSpacing: '0.12em', margin: '8px 0 0', lineHeight: 1.5 }}>
        Verify your identity (Coinbase EAS) to unlock the Minting Cockpit.
      </p>
      <button
        onClick={onClose}
        style={{
          marginTop:       18,
          padding:         '7px 22px',
          borderRadius:    6,
          border:          '1px solid #334155',
          backgroundColor: '#1e293b',
          color:           '#94a3b8',
          fontSize:        10,
          cursor:          'pointer',
          fontFamily:      'monospace',
          letterSpacing:   '0.14em',
          fontWeight:      700,
        }}
      >
        CLOSE
      </button>
    </div>
  );
}

// ─── Post-mint Receipt ─────────────────────────────────────────────────
function MintedReceipt({
  bondLabel, lens, outcomes, fastTrack, onClose,
}: {
  bondLabel: string;
  lens: RegionLens;
  outcomes: number;
  fastTrack: boolean;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <div style={{ textAlign: 'center', padding: '14px 0 4px' }}>
      <p style={{ color: '#86efac', fontSize: 13, letterSpacing: '0.06em', margin: 0, fontWeight: 700 }}>
        ✓ Truth successfully minted to the {lens} Lens.
      </p>
      <div className="cockpit-bonded-stamp" style={{
        display:       'inline-block',
        marginTop:     12,
        padding:       '4px 12px',
        border:        '1px solid #fcd34d',
        borderRadius:  4,
        color:         '#fcd34d',
        fontSize:      10,
        fontWeight:    800,
        letterSpacing: '0.22em',
        boxShadow:     '0 0 16px rgba(252,211,77,0.35)',
      }}>
        ◆ BONDED · {bondLabel}
      </div>
      <p style={{ color: '#fcd34d', fontSize: 11, letterSpacing: '0.14em', margin: '10px 0 0' }}>
        {outcomes} OUTCOMES · BOND LOCKED IN COLLATERAL CHAMBER
      </p>
      {fastTrack ? (
        <>
          <p style={{ color: '#86efac', fontSize: 10, letterSpacing: '0.12em', margin: '14px 0 0', lineHeight: 1.5 }}>
            SOVEREIGN SEAL APPLIED — MARKET IS LIVE.
          </p>
          <p style={{ color: '#334155', fontSize: 8, letterSpacing: '0.18em', margin: '10px 0 0' }}>
            §6 FAST-TRACK · AUTO-VOUCHED · {lens}
          </p>
        </>
      ) : (
        <>
          <p style={{ color: '#94a3b8', fontSize: 10, letterSpacing: '0.08em', margin: '14px 0 0', lineHeight: 1.5 }}>
            Waiting for Citizens to Vouch.
          </p>
          <p style={{ color: '#334155', fontSize: 8, letterSpacing: '0.18em', margin: '10px 0 0' }}>
            §6 · {vouchesRequiredFor(lens)} CITIZEN VOUCHES UNLOCK {lens}
          </p>
        </>
      )}
      <button
        onClick={onClose}
        style={{
          marginTop:       18,
          padding:         '7px 22px',
          borderRadius:    6,
          border:          '1px solid #78350f',
          backgroundColor: '#1a130a',
          color:           '#fcd34d',
          fontSize:        10,
          cursor:          'pointer',
          fontFamily:      'monospace',
          letterSpacing:   '0.14em',
          fontWeight:      700,
        }}
      >
        CLOSE
      </button>
    </div>
  );
}

function mintErrorMessage(reason: MintErrorReason): string {
  switch (reason) {
    case 'INSUFFICIENT_BOND_POOL':     return 'Mint bond pool below the requested stake.';
    case 'TIER_TOO_LOW':               return 'Tier 2+ (Citizen) required to mint a market.';
    case 'BOND_BELOW_MIN':             return `Creation Bond must be at least ${BOND_HARD_FLOOR} OC.`;
    case 'TITLE_TOO_SHORT':            return 'Market title must be at least 8 characters.';
    case 'TOO_FEW_OUTCOMES':           return `Need at least ${MIN_OUTCOMES} outcomes.`;
    case 'TOO_MANY_OUTCOMES':          return `Cannot exceed ${MAX_OUTCOMES} outcomes.`;
    case 'OUTCOME_LABEL_EMPTY':        return 'Every outcome needs a non-empty label.';
    case 'OUTCOME_LABELS_DUPLICATE':   return 'Outcome labels must be unique.';
    case 'RESOLUTION_HIERARCHY_EMPTY': return 'Resolution Hierarchy needs at least one source.';
    case 'PRIMARY_ORACLE_EMPTY':       return 'Primary Oracle (URL or agency) is required.';
    case 'RESOLUTION_CLAUSE_TOO_SHORT':
      return `Resolution Clause needs at least ${RESOLUTION_CLAUSE_MIN_CHARS} characters.`;
  }
}

// ─── Small bits ─────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width:           '100%',
  backgroundColor: '#1e293b',
  border:          '1px solid #334155',
  borderRadius:    6,
  color:           '#e2e8f0',
  fontSize:        11,
  padding:         '7px 10px',
  fontFamily:      'monospace',
  outline:         'none',
  boxSizing:       'border-box',
};

function Field({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.16em', display: 'block', marginBottom: 5 }}>
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ color: '#334155', fontSize: 8, letterSpacing: '0.12em', marginTop: 4, display: 'block' }}>
          {hint}
        </span>
      )}
    </label>
  );
}

// ─── Wizard Step Indicator (mobile-only) ──────────────────────────────
// Horizontal progress strip: four pips with the active one filled +
// labelled. Keeps the creator oriented through the four-step flow
// without eating the vertical budget a traditional stepper would.
function WizardStepIndicator({ step }: { step: WizardStep }): React.JSX.Element {
  const steps: WizardStep[] = [1, 2, 3, 4];
  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      gap:           6,
      padding:       '6px 0 12px',
      borderBottom:  '1px solid #1e293b',
      marginBottom:  12,
    }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {steps.map(s => {
          const done    = s < step;
          const current = s === step;
          return (
            <React.Fragment key={s}>
              <div style={{
                flex:            1,
                height:          4,
                borderRadius:    2,
                background:      current ? '#f59e0b' : done ? '#78350f' : '#1e293b',
                transition:      'background-color 0.2s',
              }} />
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{
          color:         '#fcd34d',
          fontSize:      10,
          fontWeight:    800,
          letterSpacing: '0.22em',
        }}>
          STEP {step} / 4
        </span>
        <span style={{
          color:         '#78350f',
          fontSize:      9,
          letterSpacing: '0.22em',
          fontWeight:    700,
        }}>
          · {WIZARD_STEP_LABELS[step]}
        </span>
      </div>
    </div>
  );
}

// ─── Mobile Review Panel (Step 4) ─────────────────────────────────────
// Summarises everything the creator is about to anchor — title, lens,
// outcomes, resolution stack, bond math, projected maker-fee — so the
// Finalize Mint tap is a deliberate confirmation, not a sight-unseen
// submission. Also shows the bond-locking animation when live.
function MobileReviewPanel({
  title, lens, isSovereign, outcomes, includeField,
  resolutionSource, primaryOracle, resolutionClause, parsedHierarchy,
  bond, bondDiscount, bondBase, bondBalance, bondSufficient, locking,
  estVolume, projectedMakerFee,
}: {
  title:            string;
  lens:             RegionLens;
  isSovereign:      boolean;
  outcomes:         Array<{ id: string; label: string }>;
  includeField:     boolean;
  resolutionSource: ResolutionSource;
  primaryOracle:    string;
  resolutionClause: string;
  parsedHierarchy:  string[];
  bond:             number;
  bondDiscount:     number;
  bondBase:         number;
  bondBalance:      number;
  bondSufficient:   boolean;
  locking:          boolean;
  estVolume:        number;
  projectedMakerFee: number;
}): React.JSX.Element {
  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      gap:           12,
      marginBottom:  12,
    }}>
      <div style={{
        display:       'flex',
        alignItems:    'center',
        gap:           8,
        paddingBottom: 6,
        borderBottom:  '1px solid #1e293b',
      }}>
        <span style={{ color: '#fcd34d', fontSize: 10, letterSpacing: '0.22em', fontWeight: 700 }}>
          ◆ FINAL REVIEW
        </span>
        <span style={{ color: '#334155', fontSize: 8, letterSpacing: '0.14em' }}>
          · CONFIRM AND ANCHOR
        </span>
      </div>

      {/* Collateral summary with live locking animation */}
      <CollateralChamber
        bondAmount={bond}
        bondBase={bondBase}
        bondDiscount={bondDiscount}
        balance={bondBalance}
        bondSufficient={bondSufficient}
        locking={locking}
      />

      {/* Definition recap */}
      <ReviewRow label="TITLE">
        <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 700, lineHeight: 1.35 }}>
          {title || '—'}
        </span>
      </ReviewRow>
      <ReviewRow label="LENS">
        <span style={{ color: '#fcd34d', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em' }}>
          {lens}
        </span>
        {isSovereign && (
          <span style={{ color: '#fcd34d', fontSize: 9, letterSpacing: '0.22em', marginLeft: 8 }}>
            ✦ SOVEREIGN SEAL
          </span>
        )}
      </ReviewRow>
      <ReviewRow label={`OUTCOMES · ${outcomes.length}${includeField ? ' + FIELD' : ''}`}>
        <ul style={{ margin: 0, padding: '0 0 0 16px', color: '#cbd5e1', fontSize: 12, lineHeight: 1.5 }}>
          {outcomes.map(o => (
            <li key={o.id}>{o.label}</li>
          ))}
        </ul>
      </ReviewRow>

      {/* Resolution recap */}
      <ReviewRow label="RESOLUTION SOURCE">
        <span style={{ color: '#cbd5e1', fontSize: 12 }}>{resolutionSource}</span>
      </ReviewRow>
      <ReviewRow label="PRIMARY ORACLE">
        <span style={{
          color:      '#cbd5e1',
          fontSize:   11,
          wordBreak:  'break-all',
          lineHeight: 1.4,
        }}>
          {primaryOracle || '—'}
        </span>
      </ReviewRow>
      <ReviewRow label="RESOLUTION CLAUSE">
        <span style={{ color: '#cbd5e1', fontSize: 11, lineHeight: 1.5 }}>
          {resolutionClause || '—'}
        </span>
      </ReviewRow>
      <ReviewRow label={`HIERARCHY · ${parsedHierarchy.length}`}>
        <ol style={{ margin: 0, padding: '0 0 0 18px', color: '#cbd5e1', fontSize: 11, lineHeight: 1.5 }}>
          {parsedHierarchy.map((h, i) => <li key={`${i}-${h}`}>{h}</li>)}
        </ol>
      </ReviewRow>

      {/* Economics recap */}
      <ReviewRow label="PROJECTED MAKER FEE">
        <span style={{ color: '#fde047', fontSize: 13, fontWeight: 800 }}>
          {projectedMakerFee.toLocaleString(undefined, { maximumFractionDigits: 2 })} OC
        </span>
        <span style={{ color: '#334155', fontSize: 9, letterSpacing: '0.14em', marginLeft: 6 }}>
          @ {estVolume.toLocaleString()} OC × {(MAKER_FEE_RATE * 100).toFixed(1)}%
        </span>
      </ReviewRow>
    </div>
  );
}

function ReviewRow({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{
      padding:       '8px 10px',
      borderRadius:  6,
      border:        '1px solid #1e293b',
      background:    '#0b1220',
    }}>
      <div style={{ color: '#475569', fontSize: 9, letterSpacing: '0.22em', marginBottom: 4, fontWeight: 700 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ─── Wizard Footer (mobile-only) ──────────────────────────────────────
// Sticky footer bar: Back (to previous step, or Cancel on step 1) ·
// Next / Finalize (primary, disabled until the current step is valid).
function WizardFooter({
  step, canAdvance, locking, onBack, onNext,
}: {
  step:       WizardStep;
  canAdvance: boolean;
  locking:    boolean;
  onBack:     () => void;
  onNext:     () => void;
}): React.JSX.Element {
  const isFinal   = step === 4;
  const nextLabel = isFinal
    ? (locking ? '⚒ LOCKING…' : '⚒ FINALIZE MINT')
    : 'NEXT ▸';
  const backLabel = step === 1 ? '✕ CANCEL' : '◂ BACK';
  const nextEnabled = canAdvance && !locking;
  return (
    <div style={{
      flexShrink:    0,
      display:       'grid',
      gridTemplateColumns: '1fr 1.4fr',
      gap:           10,
      padding:       '12px 2px 4px',
      borderTop:     '1px solid #1e293b',
      marginTop:     12,
      background:    '#0f172a',
    }}>
      <button
        type="button"
        onClick={onBack}
        disabled={locking}
        style={{
          minHeight:     48,
          padding:       '12px 14px',
          borderRadius:  6,
          border:        '1px solid #334155',
          background:    'transparent',
          color:         '#94a3b8',
          fontSize:      12,
          fontWeight:    700,
          letterSpacing: '0.2em',
          cursor:        locking ? 'not-allowed' : 'pointer',
          fontFamily:    'monospace',
        }}
      >
        {backLabel}
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!nextEnabled}
        style={{
          minHeight:     48,
          padding:       '12px 14px',
          borderRadius:  6,
          border:        `1px solid ${nextEnabled ? '#f59e0b' : '#78350f'}`,
          background:    nextEnabled ? '#f59e0b' : '#1a130a',
          color:         nextEnabled ? '#1a0f00' : '#78350f',
          fontSize:      12,
          fontWeight:    800,
          letterSpacing: '0.2em',
          cursor:        nextEnabled ? 'pointer' : 'not-allowed',
          fontFamily:    'monospace',
        }}
      >
        {nextLabel}
      </button>
    </div>
  );
}
